import React, { useState } from 'react';
import { Info, ExternalLink, AlertTriangle } from 'lucide-react';

interface CitationSource {
  id: string;
  title: string;
  url?: string;
  confidence: number; // 0.0-1.0
  excerpt?: string;
}

interface CitationBadgeProps {
  sources: CitationSource[];
  qualityScore: number; // 0.0-1.0
}

/**
 * Inline citation indicator with hover tooltip
 * Visual Design:
 * - Appears after Emma's response text
 * - Cosellus green accent color (#5CB800)
 * - Shows source count: "Based on [3 sources] ℹ️"
 * - Hover: Tooltip with source list
 * - Click: Expand full citation modal
 * EMMA-HARDEN-002: Cosellus branding applied
 */
export function CitationBadge({ sources, qualityScore }: CitationBadgeProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const isLowQuality = qualityScore < 0.7;

  return (
    <>
      <div className="inline-flex items-center gap-2 ml-2 relative">
        {/* Citation Badge Button */}
        <button
          onClick={() => setIsExpanded(true)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className={`
            inline-flex items-center gap-1 px-2 py-1 rounded-md text-sm
            transition-all duration-200 hover:scale-105
            ${isLowQuality 
              ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' 
              : 'bg-emma-100 text-emma-800 hover:bg-emma-200'
            }
          `}
          aria-label={`View ${sources.length} sources`}
        >
          <span className="text-xs">Based on</span>
          <strong className="text-sm">[{sources.length} source{sources.length !== 1 ? 's' : ''}]</strong>
          <Info className="w-4 h-4" />
        </button>

        {/* Low Quality Warning */}
        {isLowQuality && (
          <div className="inline-flex items-center gap-1 text-amber-600 text-xs">
            <AlertTriangle className="w-3 h-3" />
            <span>Limited confidence</span>
          </div>
        )}

        {/* Tooltip */}
        {showTooltip && (
          <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div className="text-xs font-semibold mb-2 text-gray-700">Sources:</div>
            <div className="space-y-2">
              {sources.slice(0, 3).map((source) => (
                <div key={source.id} className="text-xs">
                  <div className="font-medium text-gray-900 truncate">{source.title}</div>
                  <div className="flex items-center gap-2 text-gray-500 mt-1">
                    <span>Confidence: {(source.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
              {sources.length > 3 && (
                <div className="text-xs text-emma-600 font-medium">
                  +{sources.length - 3} more sources...
                </div>
              )}
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
              Click to view full details
            </div>
          </div>
        )}
      </div>

      {/* Citation Modal */}
      {isExpanded && (
        <CitationModal
          sources={sources}
          qualityScore={qualityScore}
          onClose={() => setIsExpanded(false)}
        />
      )}
    </>
  );
}

interface CitationModalProps {
  sources: CitationSource[];
  qualityScore: number;
  onClose: () => void;
}

function CitationModal({ sources, qualityScore, onClose }: CitationModalProps) {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-emma-600 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Citation Sources</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-emma-200 transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-2 flex items-center gap-4 text-sm">
            <span>{sources.length} sources found</span>
            <span className="px-2 py-1 bg-emma-700 rounded">
              Quality: {(qualityScore * 100).toFixed(0)}%
            </span>
          </div>
        </div>

        {/* Sources List */}
        <div className="overflow-y-auto max-h-[60vh] p-6">
          <div className="space-y-4">
            {sources.map((source, index) => (
              <div 
                key={source.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-mono text-gray-500">#{index + 1}</span>
                      <h3 className="font-semibold text-gray-900">{source.title}</h3>
                    </div>
                    {source.excerpt && (
                      <p className="text-sm text-gray-600 mb-2 italic">"{source.excerpt}"</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Confidence: {(source.confidence * 100).toFixed(0)}%</span>
                      {source.url && (
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emma-600 hover:text-emma-800 flex items-center gap-1"
                        >
                          View source <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            These sources were used by Emma to generate this response. 
            Always verify critical information independently.
          </p>
        </div>
      </div>
    </div>
  );
}
